import { graphql } from 'react-apollo';

import type { Maybe } from '__generated__/graphql-types';
import { ProductCard_ProductType as ProductCardProductType } from '__generated__/graphql-types';

import type { Recommender } from '@coursera/event-pulse-types';

import type { ConstantProductCardCourseFragmentFragment } from 'bundles/enterprise-collections/queries/fragments/__generated__/ConstantProductCardCourseFragment';
import type { ConstantProductCardS12nFragmentFragment } from 'bundles/enterprise-collections/queries/fragments/__generated__/ConstantProductCardS12nFragment';
import type { ProductCardItemFragmentFragment } from 'bundles/enterprise-collections/queries/fragments/__generated__/ProductCardItemFragment';
import FullSkillCollectionListBySkillQuery from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/FullSkillCollectionListBySkill.graphql';
import PreviewSkillCollectionListBySkillQuery from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/PreviewSkillCollectionListBySkill.graphql';
import type {
  FullSkillCollectionListBySkillQuery as FullSkillCollectionListBySkillQueryData,
  FullSkillCollectionListBySkillQueryVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/__generated__/FullSkillCollectionListBySkill';
import type {
  PreviewSkillCollectionListBySkillQuery as PreviewSkillCollectionListBySkillQueryData,
  PreviewSkillCollectionListBySkillQueryVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/__generated__/PreviewSkillCollectionListBySkill';
import type { FullCollectionListItemFragmentWithLimitsFragment } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/fragments/__generated__/FullCollectionListItemFragmentWithLimits';
import type { PreviewCollectionListItemFragmentWithLimitsFragment } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/fragments/__generated__/PreviewCollectionListItemFragmentWithLimits';

export type PreviewCollectionResult = PreviewCollectionListItemFragmentWithLimitsFragment;

export type FullCollectionResult = FullCollectionListItemFragmentWithLimitsFragment;

export type CollectionResult = FullCollectionResult | PreviewCollectionResult;

/**
 * These components are designed to work with the SkillPageContent component.
 * e.g. They rely on it's error boundary being present for error handling and the fact that we
 * have different versions for optimising page renders. Please take this into consideration if
 * you want to use for another component.
 */
export type PropsFromCaller = {
  userId: number;
  programId: string;
  skillId: string;
};

export type PropsFromPreviewSkillCollectionGraphql = {
  previewCollection?: Maybe<CollectionResult>;
  isPreviewCollectionLoading: boolean;
};

export type PropsFromFullSkillCollectionGraphql = {
  fullCollection: CollectionResult | null | undefined;
  isFullCollectionLoading: boolean;
  recsEventingData?: Recommender;
};

// This HOC fetches the recommendations by skill only
// 'Preview' fetches only the first page of information and only the data required for the product cards to optimise LCP
export const withSkillCollectionPreviewBySkill = graphql<
  PropsFromCaller,
  PreviewSkillCollectionListBySkillQueryData,
  PreviewSkillCollectionListBySkillQueryVariables,
  PropsFromPreviewSkillCollectionGraphql
>(PreviewSkillCollectionListBySkillQuery, {
  options: ({ programId, skillId }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      programId,
      skillId,
      limit: 24,
    },
    context: { clientName: 'gatewayGql' },
  }),
  props: ({ data }): PropsFromPreviewSkillCollectionGraphql => {
    if (data?.error) {
      // Caught by error boundary in SkillPageContent
      throw data?.error;
    }

    type Products = {
      items: ProductCardItemFragmentFragment[];
      courses: ConstantProductCardCourseFragmentFragment[];
      s12ns: ConstantProductCardS12nFragmentFragment[];
    };

    const collection = data?.EntCollection?.queryCurriculumCollectionBySkill.entities?.elements.reduce(
      (acc: Products, element) => {
        if (element?.__typename === 'ProductCard_ProductCard') {
          switch (element.productTypeAttributes.__typename) {
            case 'ProductCard_Course':
              return {
                ...acc,
                items: [...acc.items, { productId: 'course~' + element.id }],
                courses: [
                  ...acc.courses,
                  {
                    id: element.id,
                    name: element.productTypeAttributes.canonical.name,
                    slug: element.productTypeAttributes.canonical.slug,
                    display: true,
                    promoPhoto: element.productTypeAttributes.canonical.promoPhotoUrl,
                    partners: {
                      elements: element.productTypeAttributes.canonical.partners.map((partner) => ({
                        ...partner,
                        __typename: 'PartnersV1' as const,
                      })),
                    },
                  },
                ],
              };
            case 'ProductCard_Specialization':
              return {
                ...acc,
                items: [...acc.items, { productId: 's12n~' + element.id }],
                s12ns: [
                  ...acc.s12ns,
                  {
                    id: element.productTypeAttributes.canonical.id,
                    name: element.productTypeAttributes.canonical.name,
                    slug: element.productTypeAttributes.canonical.slug,
                    promoPhoto: element.productTypeAttributes.canonical.cardImageUrl,
                    productVariant: element.productTypeAttributes.canonical.productVariant,
                    courseIds: element.productTypeAttributes.canonical.courses.map((course) => course.id),
                    partners: {
                      elements: element.productTypeAttributes.canonical.partners.map((partner) => ({
                        ...partner,
                        __typename: 'PartnersV1' as const,
                      })),
                    },
                  },
                ],
              };
            default:
              return acc;
          }
        } else {
          return acc;
        }
      },
      { items: [], s12ns: [], courses: [] }
    );

    const courseIds = collection?.courses.map((course) => course.id) ?? [];
    const s12nIds = collection?.s12ns.map((s12n) => s12n.id) ?? [];

    const previewCollection = data?.EntCollection?.queryCurriculumCollectionBySkill
      ? {
          id: data.EntCollection.queryCurriculumCollectionBySkill?.id,
          title: data.EntCollection.queryCurriculumCollectionBySkill.enterpriseCollectionTitle ?? '',
          trackId: data.EntCollection.queryCurriculumCollectionBySkill.enterpriseCollectionTrackingId ?? '',
          courseIds,
          s12nIds,
          courses: collection?.courses ? { elements: collection.courses } : null,
          s12ns: collection?.s12ns ? { elements: collection.s12ns } : null,
          items: collection?.items ?? [],
        }
      : undefined;

    return {
      previewCollection,
      isPreviewCollectionLoading: data?.loading ?? true,
    };
  },
});

// Getting the old data shape expected from the new one
export const getFullCollectionData = (data?: FullSkillCollectionListBySkillQueryData) => {
  type Products = {
    items: ProductCardItemFragmentFragment[];
    courses: ConstantProductCardCourseFragmentFragment[];
    s12ns: ConstantProductCardS12nFragmentFragment[];
  };

  return data?.EntCollection?.queryCurriculumCollectionBySkill.entities?.elements.reduce(
    (acc: Products, element) => {
      if (element?.__typename === 'ProductCard_ProductCard') {
        switch (element.productTypeAttributes.__typename) {
          case 'ProductCard_Course':
            return {
              ...acc,
              items: [...acc.items, { productId: 'course~' + element.id }],
              courses: [
                ...acc.courses,
                {
                  id: element.id,
                  name: element.productTypeAttributes.canonical.name,
                  slug: element.productTypeAttributes.canonical.slug,
                  display: true,
                  promoPhoto: element.productTypeAttributes.canonical.promoPhotoUrl,
                  partners: {
                    elements: element.productTypeAttributes.canonical.partners.map((partner) => ({
                      ...partner,
                      __typename: 'PartnersV1' as const,
                    })),
                  },
                  primaryLanguages: element.productTypeAttributes.canonical.primaryLanguageCodes,
                  subtitleLanguages: element.productTypeAttributes.canonical.subtitleLanguageCodes,
                  domainTypes: element.productTypeAttributes.canonical.domains.map((domain) => ({
                    domainId: domain.domainSlug,
                  })),
                  level: element.productTypeAttributes.canonical.courseDifficultyLevel,
                  courseTypeMetadata: {
                    id: element.id,
                    courseTypeMetadata: {
                      __typename:
                        element.marketingProductType === ProductCardProductType.GuidedProject
                          ? ('CourseTypeMetadataV1_rhymeProjectMember' as const)
                          : ('CourseTypeMetadataV1_standardCourseMember' as const),
                    },
                  },
                },
              ],
            };
          case 'ProductCard_Specialization':
            return {
              ...acc,
              items: [...acc.items, { productId: 's12n~' + element.id }],
              s12ns: [
                ...acc.s12ns,
                {
                  id: element.productTypeAttributes.canonical.id,
                  name: element.productTypeAttributes.canonical.name,
                  slug: element.productTypeAttributes.canonical.slug,
                  promoPhoto: element.productTypeAttributes.canonical.cardImageUrl,
                  courseIds: element.productTypeAttributes.canonical.courses.map((course) => course.id),
                  courses: {
                    elements: element.productTypeAttributes.canonical.courses.map((course) => ({
                      id: course.id,
                      primaryLanguages: course.primaryLanguageCodes,
                      level: course.difficultyLevel,
                    })),
                    paging: {},
                  },
                  partners: {
                    elements: element.productTypeAttributes.canonical.partners.map((partner) => ({
                      ...partner,
                      __typename: 'PartnersV1' as const,
                    })),
                  },
                  productVariant:
                    element.marketingProductType === ProductCardProductType.ProfessionalCertificate
                      ? 'ProfessionalCertificateS12n'
                      : 'NormalS12n',
                  domainTypes: [
                    { domainId: element.productTypeAttributes.canonical.primaryDomainPath.domain.id },
                    { domainId: element.productTypeAttributes.canonical.secondaryDomainPath?.domain.id },
                  ],
                  metadata: { level: element.productTypeAttributes.canonical.s12nDifficultyLevel },
                },
              ],
            };
          default:
            return acc;
        }
      } else {
        return acc;
      }
    },
    { items: [], s12ns: [], courses: [] }
  );
};

// 'Full' fetches all the information required for the filters
export const withFullSkillCollectionBySkill = graphql<
  PropsFromCaller,
  FullSkillCollectionListBySkillQueryData,
  FullSkillCollectionListBySkillQueryVariables,
  PropsFromFullSkillCollectionGraphql
>(FullSkillCollectionListBySkillQuery, {
  options: ({ programId, skillId }) => ({
    ssr: false, // Should always run after initial render
    notifyOnNetworkStatusChange: true,
    variables: {
      programId,
      skillId,
      limit: 100,
    },
    context: { clientName: 'gatewayGql' },
  }),
  props: ({ data }): PropsFromFullSkillCollectionGraphql => {
    if (data?.error) {
      // Caught by error boundary in SkillPageContent
      throw data?.error;
    }

    const collection = getFullCollectionData(data);
    const courseIds = collection?.courses.map((course) => course.id) ?? [];
    const s12nIds = collection?.s12ns.map((s12n) => s12n.id) ?? [];

    const fullCollection = data?.EntCollection?.queryCurriculumCollectionBySkill
      ? {
          id: data.EntCollection.queryCurriculumCollectionBySkill?.id,
          title: data.EntCollection.queryCurriculumCollectionBySkill.enterpriseCollectionTitle ?? '',
          trackId: data.EntCollection.queryCurriculumCollectionBySkill.enterpriseCollectionTrackingId ?? '',
          courseIds,
          s12nIds,
          courses: collection?.courses ? { elements: collection.courses } : null,
          s12ns: collection?.s12ns ? { elements: collection.s12ns } : null,
          items: collection?.items ?? [],
        }
      : undefined;

    const recsEventingData = data?.EntCollection?.queryCurriculumCollectionBySkill && {
      hash: data.EntCollection.queryCurriculumCollectionBySkill?.recommenderConfigurationHash ?? '',
      context: data.EntCollection.queryCurriculumCollectionBySkill?.context,
    };

    return {
      fullCollection,
      isFullCollectionLoading: data?.loading ?? true,
      recsEventingData,
    };
  },
});
